/**
 * Contract v2 (Ultra finding 1): the split model.
 *
 *  - `SiteDefinition` — PIPELINE-OWNED. Page/section order, pattern types +
 *    variants, freeform templates (immutable html+css), editable manifests,
 *    module mounts, head contributions. Editing surfaces can never write it.
 *  - `ContentSnapshot` — the ONLY editable document. Typed values keyed by
 *    (sectionId, fieldId) bound to manifest entries, plus module data keyed
 *    by instanceId. Structurally incapable of carrying html/css/order/
 *    variants — safety by construction, verified by tests.
 *
 * Rendering resolves Definition + Snapshot into the legacy SiteContent
 * shape (values applied at manifest paths / data-edit markers), so the
 * proven renderer and its invariants are reused unchanged.
 */
import * as cheerio from 'cheerio';
import { z } from 'zod';
import {
  checkHeadContribution,
  checkSectionCss,
  isAllowedUrl,
  isCleanRichText,
  sanitizeRichText,
} from '../policy/index.js';
import { checkFreeformTemplate, checkMarkerPlacement, ensureRootAnchorId, sanitizeFreeformTemplate } from '../policy/html.js';
import patternCatalog from './pattern-catalog.json' with { type: 'json' };
import {
  FreeformManifestEntry,
  ImageValue,
  JsonValue,
  LinkValue,
  NavItem,
  PatternManifestEntry,
  PatternType,
  SiteMeta,
  type FreeformSectionT,
  type JsonValueT,
  type PatternSectionT,
  type SiteContentT,
} from './types.js';
import { resolveFieldPath } from './validate.js';

const slug = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

const CATALOG = patternCatalog as Record<string, { variants: string[]; primary: string }>;

/** Definition-side pattern section: baseline fields + manifest. */
export const DefPatternSection = z.object({
  id: slug,
  anchor: slug,
  kind: z.literal('pattern'),
  type: PatternType,
  variant: z.string().min(1).max(60),
  /** Pipeline-authored baseline (config + initial content). */
  fields: z.record(z.string(), JsonValue),
  editableManifest: z.array(PatternManifestEntry),
});

/** Definition-side freeform section: immutable template + manifest. */
export const DefFreeformSection = z.object({
  id: slug,
  anchor: slug,
  kind: z.literal('freeform'),
  /** Immutable template markup carrying data-edit markers. */
  html: z.string().min(1),
  css: z.string().optional(),
  editableManifest: z.array(FreeformManifestEntry),
});

/** Module mount: a fixed slot rendered by a registry module's view. */
export const DefModuleMount = z.object({
  id: slug,
  anchor: slug,
  kind: z.literal('module'),
  // Linear/unambiguous (no ReDoS): `-` is a separator only, never a segment
  // char, so it can't overlap the `[.-]` separator. Matches venue.menu-hours.
  moduleId: z.string().regex(/^[a-z]+(?:[.-][a-z]+)*$/),
  instanceId: slug,
  view: slug,
  /** Pipeline-owned module config (locale, form endpoint, …); validated
   *  against the module's configSchema at resolve time. */
  config: JsonValue.optional(),
});

export const DefSection = z.discriminatedUnion('kind', [DefPatternSection, DefFreeformSection, DefModuleMount]);

export const DefPage = z.object({
  id: slug,
  path: z.string().regex(/^\/(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*)?$/),
  title: z.string().min(1).max(200),
  metaDescription: z.string().max(320).optional(),
  sections: z.array(DefSection).min(1),
});

export const HeadContributionSchema = z.object({
  stylesheets: z.array(z.string()).optional(),
  preconnect: z.array(z.string()).optional(),
});

export const SiteDefinition = z.object({
  contractVersion: z.literal(2),
  site: SiteMeta,
  nav: z.object({ items: z.array(NavItem) }),
  head: HeadContributionSchema.optional(),
  pages: z.array(DefPage).min(1),
});

/** Typed field values — the entire writable universe of an editing surface.
 *  Each member is `.strict()` so no extra keys (html/css/variant/…) can ride
 *  along inside a value object. */
export const FieldValue = z.union([
  z.object({ type: z.literal('text'), value: z.string() }).strict(),
  z.object({ type: z.literal('richtext'), value: z.string() }).strict(),
  z.object({ type: z.literal('image'), value: ImageValue }).strict(),
  z.object({ type: z.literal('link'), value: LinkValue }).strict(),
  z.object({ type: z.literal('list'), value: z.array(JsonValue) }).strict(),
  z.object({ type: z.literal('hours'), value: z.array(JsonValue) }).strict(),
  z.object({ type: z.literal('menu_items'), value: z.array(JsonValue) }).strict(),
]);

/**
 * The editable document. `.strict()` at the top level so a snapshot cannot
 * carry stray html/css/order/variant keys — the write contract is enforced by
 * the schema itself, not merely by a validator that a caller might skip.
 */
export const ContentSnapshot = z
  .object({
    contractVersion: z.literal(2),
    /** values[sectionId][fieldId] — bound to that section's manifest entry. */
    values: z.record(slug, z.record(slug, FieldValue)),
    /** Module data keyed by mount instanceId; shape owned by the module's dataSchema. */
    modules: z.record(slug, z.object({ dataVersion: z.number().int().positive(), data: JsonValue }).strict()),
  })
  .strict();

export type DefPatternSectionT = z.infer<typeof DefPatternSection>;
export type DefFreeformSectionT = z.infer<typeof DefFreeformSection>;
export type DefModuleMountT = z.infer<typeof DefModuleMount>;
export type DefSectionT = z.infer<typeof DefSection>;
export type SiteDefinitionT = z.infer<typeof SiteDefinition>;
export type FieldValueT = z.infer<typeof FieldValue>;
export type ContentSnapshotT = z.infer<typeof ContentSnapshot>;

export interface SplitIssue {
  code: string;
  where: string;
  message: string;
}

/** Closed-catalog variant check + manifest paths must resolve in fields. */
function checkPatternSectionDef(section: DefPatternSectionT, where: string, issues: SplitIssue[]): void {
  const entry = CATALOG[section.type];
  if (!entry) {
    issues.push({ code: 'unknown-pattern-type', where, message: `no catalog entry for "${section.type}"` });
  } else if (!entry.variants.includes(section.variant)) {
    issues.push({
      code: 'unknown-variant',
      where,
      message: `variant "${section.variant}" not in catalog for ${section.type} (${entry.variants.join(', ')})`,
    });
  }
  for (const manifestEntry of section.editableManifest) {
    if (resolveFieldPath(section.fields, manifestEntry.path) === undefined) {
      issues.push({ code: 'manifest-path-unresolved', where: `${where}/${manifestEntry.fieldId}`, message: `path ${manifestEntry.path} does not resolve in fields` });
    }
  }
}

interface BoundField {
  type: string;
  constraints?: { maxLength?: number | undefined; minItems?: number | undefined; maxItems?: number | undefined } | undefined;
}

function manifestIndex(section: DefSectionT): Map<string, BoundField> {
  const map = new Map<string, BoundField>();
  if (section.kind === 'module') return map;
  for (const entry of section.editableManifest) map.set(entry.fieldId, { type: entry.type, constraints: entry.constraints });
  return map;
}

/** Optional module-data validator (registry-aware), injected to avoid a
 *  schema -> modules layering cycle. Returns an issue message or null. */
export type ModuleDataValidator = (mount: DefModuleMountT, data: { dataVersion: number; data: JsonValueT } | undefined) => string | null;

function checkConstraints(value: FieldValueT, constraints: BoundField['constraints'], where: string, issues: SplitIssue[]): void {
  if (!constraints) return;
  if ((value.type === 'text' || value.type === 'richtext') && constraints.maxLength !== undefined && value.value.length > constraints.maxLength) {
    issues.push({ code: 'constraint-max-length', where, message: `value length ${value.value.length} exceeds maxLength ${constraints.maxLength}` });
  }
  if ((value.type === 'list' || value.type === 'hours' || value.type === 'menu_items') && Array.isArray(value.value)) {
    if (constraints.minItems !== undefined && value.value.length < constraints.minItems) {
      issues.push({ code: 'constraint-min-items', where, message: `${value.value.length} items is below minItems ${constraints.minItems}` });
    }
    if (constraints.maxItems !== undefined && value.value.length > constraints.maxItems) {
      issues.push({ code: 'constraint-max-items', where, message: `${value.value.length} items exceeds maxItems ${constraints.maxItems}` });
    }
  }
}

function checkFieldValuePolicy(value: FieldValueT, bound: BoundField, where: string, issues: SplitIssue[]): void {
  if (value.type === 'richtext' && !isCleanRichText(value.value)) {
    issues.push({ code: 'richtext-policy-violation', where, message: 'richtext value does not conform to the content policy' });
  } else if (value.type === 'link' && !isAllowedUrl(value.value.href)) {
    issues.push({ code: 'link-url-rejected', where, message: `link href "${value.value.href.slice(0, 60)}" is not an allowed URL` });
  } else if (value.type === 'image' && !isAllowedUrl(value.value.src)) {
    issues.push({ code: 'image-url-rejected', where, message: `image src "${value.value.src.slice(0, 60)}" is not an allowed URL` });
  }
  checkConstraints(value, bound.constraints, where, issues);
}

/**
 * Validate a snapshot AGAINST its definition: every value must bind to an
 * existing manifest entry of the matching type and pass the content policy
 * (richtext sanitized, image/link URLs allowed). When a `moduleData`
 * validator is supplied, module payloads are validated against the mounted
 * module's schema too. Returns all issues (never throws).
 */
export function validateSnapshotAgainstDefinition(
  definition: SiteDefinitionT,
  snapshot: ContentSnapshotT,
  moduleData?: ModuleDataValidator,
): SplitIssue[] {
  const issues: SplitIssue[] = [];
  const sectionsById = new Map<string, DefSectionT>();
  for (const page of definition.pages) {
    for (const section of page.sections) sectionsById.set(section.id, section);
  }

  for (const [sectionId, fields] of Object.entries(snapshot.values)) {
    const section = sectionsById.get(sectionId);
    if (!section) {
      issues.push({ code: 'unknown-section', where: sectionId, message: 'snapshot addresses a section not in the definition' });
      continue;
    }
    const manifest = manifestIndex(section);
    for (const [fieldId, value] of Object.entries(fields)) {
      const bound = manifest.get(fieldId);
      const where = `${sectionId}/${fieldId}`;
      if (!bound) {
        issues.push({ code: 'unknown-field', where, message: 'fieldId not in the section manifest' });
        continue;
      }
      if (bound.type !== value.type) {
        issues.push({ code: 'field-type-mismatch', where, message: `manifest says ${bound.type}, value is ${value.type}` });
        continue;
      }
      checkFieldValuePolicy(value, bound, where, issues);
    }
  }

  const mountsByInstance = new Map<string, DefModuleMountT>();
  for (const section of sectionsById.values()) {
    if (section.kind === 'module') mountsByInstance.set(section.instanceId, section);
  }
  for (const [instanceId, data] of Object.entries(snapshot.modules)) {
    const mount = mountsByInstance.get(instanceId);
    if (!mount) {
      issues.push({ code: 'unknown-module-instance', where: instanceId, message: 'module data without a matching mount' });
      continue;
    }
    if (moduleData) {
      const problem = moduleData(mount, data);
      if (problem) issues.push({ code: 'module-data-invalid', where: instanceId, message: problem });
    }
  }
  // Mounts whose module data is entirely missing (a mount with no snapshot
  // entry). Object.hasOwn — never an inherited "constructor"/"toString" key.
  for (const [instanceId, mount] of mountsByInstance) {
    if (!Object.hasOwn(snapshot.modules, instanceId) && moduleData) {
      const problem = moduleData(mount, undefined);
      if (problem) issues.push({ code: 'module-data-missing', where: instanceId, message: problem });
    }
  }
  return issues;
}

function checkFreeformSectionDef(section: DefFreeformSectionT, where: string, issues: SplitIssue[]): void {
  // Freeform root must carry its own anchor as id, else in-page nav breaks silently.
  const root = cheerio.load(section.html, null, false).root().children().first();
  if (root.attr('id') !== section.anchor) {
    issues.push({ code: 'freeform-missing-anchor-id', where, message: `freeform root must have id="${section.anchor}"` });
  }
  for (const issue of checkFreeformTemplate(section.html)) {
    issues.push({ code: `freeform-${issue.code}`, where, message: issue.detail });
  }
  for (const issue of checkMarkerPlacement(section.html, section.editableManifest)) {
    issues.push({ code: issue.code, where: `${where}/${issue.fieldId}`, message: issue.detail });
  }
  if (section.css !== undefined) {
    for (const issue of checkSectionCss(section.css)) {
      issues.push({ code: `css-${issue.code}`, where, message: issue.detail });
    }
  }
}

/**
 * Validate definition-side invariants above the zod shape: unique section ids
 * (globally — snapshot values key by section id), unique field ids per
 * section, unique page paths, pattern variants against the closed catalog,
 * freeform template safety + marker placement + CSS policy, and the typed
 * head contribution. Returns all issues (never throws).
 */
export function validateDefinition(definition: SiteDefinitionT): SplitIssue[] {
  const issues: SplitIssue[] = [];
  const sectionIds = new Set<string>();
  const instanceModuleIds = new Map<string, string>();
  const pagePaths = new Set<string>();

  const pageIds = new Set<string>();

  definition.pages.forEach((page, pageIndex) => {
    if (pagePaths.has(page.path)) {
      issues.push({ code: 'duplicate-page-path', where: `pages/${pageIndex}`, message: `duplicate page path "${page.path}"` });
    }
    pagePaths.add(page.path);
    if (pageIds.has(page.id)) {
      issues.push({ code: 'duplicate-page-id', where: `pages/${pageIndex}`, message: `duplicate page id "${page.id}"` });
    }
    pageIds.add(page.id);

    const pageAnchors = new Set<string>();
    page.sections.forEach((section, sectionIndex) => {
      const where = `pages/${pageIndex}/sections/${sectionIndex}`;
      if (sectionIds.has(section.id)) {
        issues.push({ code: 'duplicate-section-id', where, message: `section id "${section.id}" is used more than once (must be globally unique)` });
      }
      sectionIds.add(section.id);
      if (pageAnchors.has(section.anchor)) {
        issues.push({ code: 'duplicate-anchor', where, message: `duplicate anchor "${section.anchor}" on page "${page.path}"` });
      }
      pageAnchors.add(section.anchor);

      if (section.kind !== 'module') {
        const fieldIds = new Set<string>();
        for (const entry of section.editableManifest) {
          if (fieldIds.has(entry.fieldId)) {
            issues.push({ code: 'duplicate-field-id', where, message: `duplicate fieldId "${entry.fieldId}" in section "${section.id}"` });
          }
          fieldIds.add(entry.fieldId);
        }
      } else {
        // A module instanceId keys snapshot data and drives operation dispatch.
        // The SAME instance may be mounted through several views (a venue's
        // menu-card AND hours views legitimately share one data blob), so a
        // shared instanceId is allowed — but every mount sharing it MUST name
        // the SAME moduleId, else applyModuleOperation's first-match mount and
        // validateSnapshotAgainstDefinition's last-match mount index could
        // resolve the instance to DIFFERENT modules. Enforcing one module per
        // instanceId makes those two provably agree.
        const priorModuleId = instanceModuleIds.get(section.instanceId);
        if (priorModuleId === undefined) {
          instanceModuleIds.set(section.instanceId, section.moduleId);
        } else if (priorModuleId !== section.moduleId) {
          issues.push({ code: 'duplicate-instance-id', where, message: `module instanceId "${section.instanceId}" is mounted for both "${priorModuleId}" and "${section.moduleId}" (one instanceId must map to exactly one module)` });
        }
      }
      if (section.kind === 'pattern') checkPatternSectionDef(section, where, issues);
      else if (section.kind === 'freeform') checkFreeformSectionDef(section, where, issues);
    });
  });

  if (definition.head) {
    for (const issue of checkHeadContribution(definition.head)) {
      issues.push({ code: `head-${issue.code}`, where: 'head', message: issue.detail });
    }
  }
  return issues;
}

function applyPatternValues(section: DefPatternSectionT, values: Record<string, FieldValueT>): PatternSectionT {
  const fields = structuredClone(section.fields) as Record<string, JsonValueT>;
  for (const entry of section.editableManifest) {
    const value = values[entry.fieldId];
    if (!value) continue;
    setFieldPath(fields, entry.path, valueToJson(value));
  }
  return {
    id: section.id,
    anchor: section.anchor,
    kind: 'pattern',
    type: section.type,
    variant: section.variant,
    fields,
    editableManifest: section.editableManifest,
  };
}

function valueToJson(value: FieldValueT): JsonValueT {
  return value.value as JsonValueT;
}

/** Path segments that would climb the prototype chain — never traversed. */
const FORBIDDEN_PATH_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

/** A canonical, in-bounds array index (no `-1`, `01`, `1e3`, `Infinity`). */
function canonicalIndex(part: string, length: number): number | null {
  if (!/^(?:0|[1-9][0-9]*)$/.test(part)) return null;
  const index = Number(part);
  return index < length ? index : null;
}

/**
 * Set a JSON-pointer-style path (parent must already exist — bound by the
 * manifest). Refuses prototype-chain keys (prototype-pollution guard) and
 * only writes own, in-bounds locations; anything else is a safe no-op.
 */
function setFieldPath(fields: Record<string, JsonValueT>, path: string, value: JsonValueT): void {
  const parts = path.split('/').slice(1);
  if (parts.some((part) => FORBIDDEN_PATH_KEYS.has(part))) return;
  let parent: JsonValueT = fields;
  for (const part of parts.slice(0, -1)) {
    if (parent === null || typeof parent !== 'object') return;
    if (Array.isArray(parent)) {
      const index = canonicalIndex(part, parent.length);
      if (index === null) return;
      parent = parent[index] as JsonValueT;
    } else {
      if (!Object.hasOwn(parent, part)) return;
      parent = (parent as Record<string, JsonValueT>)[part]!;
    }
  }
  const leaf = parts[parts.length - 1]!;
  if (parent === null || typeof parent !== 'object') return;
  if (Array.isArray(parent)) {
    const index = canonicalIndex(leaf, parent.length);
    if (index !== null) parent[index] = value;
  } else if (Object.hasOwn(parent, leaf)) {
    (parent as Record<string, JsonValueT>)[leaf] = value;
  }
}

/**
 * Apply typed snapshot values into a freeform template. Safe by construction,
 * independent of any prior validation call:
 *  - branches on the MANIFEST entry type (trusted), requiring the value to
 *    match — a mismatched value is ignored, never routed to a raw-html sink;
 *  - richtext is sanitized HERE (defense in depth), not trusted as pre-clean;
 *  - image/link URLs pass the canonical URL policy before being written;
 *  - image src replacement clears stale srcset/sizes so the browser can't
 *    keep loading the old/off-site source.
 */
function applyFreeformValues(section: DefFreeformSectionT, values: Record<string, FieldValueT>): FreeformSectionT {
  const $ = cheerio.load(section.html, null, false);
  for (const entry of section.editableManifest) {
    const value = values[entry.fieldId];
    if (!value || value.type !== entry.type) continue;
    const node = $(`[data-edit="${entry.fieldId}"]`).first();
    if (node.length === 0) continue;
    if (entry.type === 'image' && value.type === 'image' && node.is('img')) {
      if (isAllowedUrl(value.value.src)) node.attr('src', value.value.src);
      node.attr('alt', value.value.alt);
      node.removeAttr('srcset');
      node.removeAttr('sizes');
    } else if (entry.type === 'link' && value.type === 'link' && node.is('a')) {
      if (isAllowedUrl(value.value.href)) node.attr('href', value.value.href);
      node.text(value.value.text);
    } else if (entry.type === 'richtext' && value.type === 'richtext') {
      node.html(sanitizeRichText(value.value));
    } else if (entry.type === 'text' && value.type === 'text') {
      node.text(value.value);
    }
  }
  const result: FreeformSectionT = {
    id: section.id,
    anchor: section.anchor,
    kind: 'freeform',
    html: $.html(),
    editableManifest: section.editableManifest,
  };
  if (section.css !== undefined) result.css = section.css;
  return result;
}

/**
 * Resolve Definition + Snapshot into the legacy SiteContent document the
 * renderer consumes. Module mounts require a resolver (injected by the
 * module kernel); without one, mounts throw — a definition with mounts
 * cannot silently render empty.
 */
export function resolveToContent(
  definition: SiteDefinitionT,
  snapshot: ContentSnapshotT,
  resolveModule?: (mount: DefModuleMountT, moduleData: { dataVersion: number; data: JsonValueT } | undefined) => FreeformSectionT,
): SiteContentT {
  return {
    schemaVersion: 1,
    site: definition.site,
    nav: definition.nav,
    pages: definition.pages.map((page) => ({
      id: page.id,
      path: page.path,
      title: page.title,
      ...(page.metaDescription !== undefined ? { metaDescription: page.metaDescription } : {}),
      sections: page.sections.map((section) => {
        const values = Object.hasOwn(snapshot.values, section.id) ? snapshot.values[section.id]! : {};
        if (section.kind === 'pattern') return applyPatternValues(section, values);
        if (section.kind === 'freeform') return applyFreeformValues(section, values);
        if (!resolveModule) throw new Error(`module mount "${section.id}" (${section.moduleId}) has no resolver`);
        const moduleData = Object.hasOwn(snapshot.modules, section.instanceId) ? snapshot.modules[section.instanceId] : undefined;
        return resolveModule(section, moduleData);
      }),
    })),
  };
}

/**
 * v1 → v2 migration: split a legacy combined document into Definition +
 * initial Snapshot. Pattern sections: manifest-path values are extracted
 * into the snapshot (definition keeps them as baseline too — resolve is
 * idempotent). Freeform sections: current marker contents become initial
 * text/image values; the html stays as the immutable template.
 */
/** Extract the initial editable value for one manifest entry of a section. */
function extractLegacyValue(
  section: SiteContentT['pages'][number]['sections'][number],
  entry: { fieldId: string; type: string; path?: string },
  $: cheerio.CheerioAPI | null,
): FieldValueT | undefined {
  if (section.kind === 'pattern') {
    const current = resolveFieldPath(section.fields, entry.path ?? '');
    return current === undefined ? undefined : jsonToValue(entry.type, current);
  }
  const node = $!(`[data-edit="${entry.fieldId}"]`).first();
  if (node.length === 0) return undefined;
  if (entry.type === 'image' && node.is('img')) {
    return { type: 'image', value: { src: node.attr('src') ?? '', alt: node.attr('alt') ?? '' } };
  }
  // Markers holding inline children (<strong>/<a>/…) stay template-only;
  // extracting them as plain text would flatten the markup on first resolve.
  if (entry.type === 'text' && node.children().length === 0) {
    return { type: 'text', value: node.text() };
  }
  return undefined;
}

function extractLegacySnapshotValues(content: SiteContentT): Record<string, Record<string, FieldValueT>> {
  const values: Record<string, Record<string, FieldValueT>> = {};
  for (const page of content.pages) {
    for (const section of page.sections) {
      const $ = section.kind === 'freeform' ? cheerio.load(section.html, null, false) : null;
      const sectionValues: Record<string, FieldValueT> = {};
      for (const entry of section.editableManifest) {
        const value = extractLegacyValue(section, entry, $);
        if (value) sectionValues[entry.fieldId] = value;
      }
      if (Object.keys(sectionValues).length > 0) values[section.id] = sectionValues;
    }
  }
  return values;
}

function buildDefinitionFromLegacy(content: SiteContentT): SiteDefinitionT {
  return {
    contractVersion: 2,
    site: content.site,
    nav: content.nav,
    pages: content.pages.map((page) => ({
      id: page.id,
      path: page.path,
      title: page.title,
      ...(page.metaDescription !== undefined ? { metaDescription: page.metaDescription } : {}),
      sections: page.sections.map((section): DefSectionT => (section.kind === 'pattern'
        ? {
            id: section.id,
            anchor: section.anchor,
            kind: 'pattern',
            type: section.type,
            variant: section.variant,
            fields: section.fields,
            editableManifest: section.editableManifest,
          }
        : {
            id: section.id,
            anchor: section.anchor,
            kind: 'freeform',
            // Legacy markup is UNTRUSTED (real WP extraction) — sanitize it
            // before blessing it as a definition template, and guarantee the
            // root carries its anchor id so in-page nav resolves.
            html: ensureRootAnchorId(sanitizeFreeformTemplate(section.html), section.anchor),
            ...(section.css !== undefined ? { css: section.css } : {}),
            editableManifest: section.editableManifest,
          })),
    })),
  };
}

/**
 * v1 -> v2 migration: split a legacy combined document into a sanitized
 * Definition + initial Snapshot. Freeform templates are run through the
 * template sanitizer (legacy WP markup is untrusted); current marker
 * contents become the initial values.
 */
export function splitLegacy(content: SiteContentT): { definition: SiteDefinitionT; snapshot: ContentSnapshotT } {
  return {
    definition: buildDefinitionFromLegacy(content),
    snapshot: { contractVersion: 2, values: extractLegacySnapshotValues(content), modules: {} },
  };
}

function jsonToValue(type: string, current: JsonValueT): FieldValueT | undefined {
  switch (type) {
    case 'text':
      return typeof current === 'string' ? { type: 'text', value: current } : undefined;
    case 'richtext':
      return typeof current === 'string' ? { type: 'richtext', value: current } : undefined;
    case 'image': {
      const parsed = ImageValue.safeParse(current);
      return parsed.success ? { type: 'image', value: parsed.data } : undefined;
    }
    case 'link': {
      const parsed = LinkValue.safeParse(current);
      return parsed.success ? { type: 'link', value: parsed.data } : undefined;
    }
    case 'list':
      return Array.isArray(current) ? { type: 'list', value: current } : undefined;
    case 'hours':
      return Array.isArray(current) ? { type: 'hours', value: current } : undefined;
    case 'menu_items':
      return Array.isArray(current) ? { type: 'menu_items', value: current } : undefined;
    default:
      return undefined;
  }
}
